import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

function formatTime(milliseconds) {
  if (milliseconds === null || milliseconds === undefined || !Number.isFinite(Number(milliseconds))) {
    return null;
  }
  return `${(Number(milliseconds) / 1000).toFixed(2)}s`;
}

function playerColor(index) {
  const palette = ['#22c55e', '#3b82f6', '#f97316', '#ef4444', '#a855f7', '#06b6d4', '#f59e0b', '#ec4899'];
  return palette[index % palette.length];
}

export default function SpotTheBallGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const frameRef = useRef(null);
  const imageWrapRef = useRef(null);

  const [pendingSpot, setPendingSpot] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ value: gameState.timeLimit || 20, round: -1 });

  const state = useMemo(() => gameState || {}, [gameState]);
  const phase = state.phase || 'guessing';
  const finished = !!state.finished;
  const roundIndex = Number.isFinite(Number(state.roundIndex)) ? Number(state.roundIndex) : 0;
  const rounds = Number.isFinite(Number(state.rounds)) ? Number(state.rounds) : 0;
  const timeLimit = Number.isFinite(Number(state.timeLimit)) ? Number(state.timeLimit) : 20;
  const meId = user ? (user.socketId || user.userId) : null;
  const guessedPlayers = Array.isArray(state.guessedPlayers) ? state.guessedPlayers : [];
  const mySubmitted = meId ? guessedPlayers.includes(meId) : false;

  const revealRows = Array.isArray(state.roundResults) ? state.roundResults : [];
  const rankings = Array.isArray(state.rankings) ? state.rankings : [];
  const image = state.currentImage && state.currentImage.image ? state.currentImage.image : null;
  const revealedBall = state.revealedBall || null;

  const dispatchAction = async (action) => {
    if (typeof onGameAction === 'function') {
      return onGameAction(action);
    }
    return submitAction(action);
  };

  useEffect(() => {
    setPendingSpot(null);
  }, [roundIndex]);

  useEffect(() => {
    setTimeLeft({ value: timeLimit, round: roundIndex });
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    if (phase !== 'guessing' || finished) return undefined;

    const start = Number.isFinite(Number(state.startTime)) ? Number(state.startTime) : Date.now();
    const tick = () => {
      const elapsed = (Date.now() - start) / 1000;
      setTimeLeft({ value: Math.max(0, Math.ceil(timeLimit - elapsed)), round: roundIndex });
      if (elapsed < timeLimit && phase === 'guessing' && !finished) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [phase, finished, roundIndex, timeLimit, state.startTime]);

  const handleImageClick = (event) => {
    if (finished || phase !== 'guessing' || mySubmitted || !imageWrapRef.current) return;
    const rect = imageWrapRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
    setPendingSpot({ x, y });
  };

  const submitSpot = async () => {
    if (!pendingSpot || mySubmitted || phase !== 'guessing' || finished) return;
    await dispatchAction({ type: 'submit-spot', x: pendingSpot.x, y: pendingSpot.y });
  };

  const markerStyle = (spot) => ({
    left: `${spot.x * 100}%`,
    top: `${spot.y * 100}%`,
    transform: 'translate(-50%, -50%)',
  });

  const revealGuessRows = revealRows.filter((entry) => entry.hadGuess && entry.guess);

  return (
    <div className="w-full rounded-[28px] border border-slate-200 bg-white p-4 text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-300">Spot The Ball</div>
          <div className="text-lg font-semibold">Place your marker where the missing football should be.</div>
          <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">Guesses stay hidden until reveal.</div>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-[0.12em]">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700 dark:bg-slate-800 dark:text-slate-200">Round {Math.min(roundIndex + 1, rounds || 0)} / {rounds || 0}</span>
          <span className="rounded-full bg-sky-100 px-3 py-1 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200">{phase}</span>
          <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200">{guessedPlayers.length}/{gamePlayers.length || 0} submitted</span>
        </div>
      </div>

      <div className="mb-4 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Image</div>
        <div
          ref={imageWrapRef}
          className="relative mt-3 overflow-hidden rounded-2xl border border-slate-300 bg-slate-200 dark:border-slate-600 dark:bg-slate-950"
          onClick={handleImageClick}
          role="button"
          tabIndex={0}
          onKeyDown={() => {}}
          style={{ aspectRatio: '16 / 9', cursor: phase === 'guessing' && !mySubmitted ? 'crosshair' : 'default' }}
        >
          {image ? (
            <img src={image} alt="Spot the Ball round" className="h-full w-full object-cover" draggable={false} />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-slate-500 dark:text-slate-400">No image available for this round.</div>
          )}

          {phase === 'guessing' && pendingSpot ? (
            <div className="absolute h-5 w-5 rounded-full border-2 border-white bg-emerald-500 shadow" style={markerStyle(pendingSpot)} />
          ) : null}

          {phase !== 'guessing' && revealedBall ? (
            <div className="absolute h-6 w-6 rounded-full border-2 border-white bg-red-500 shadow" style={markerStyle(revealedBall)} title="Actual ball position" />
          ) : null}

          {phase !== 'guessing'
            ? revealGuessRows.map((entry, index) => (
              <div
                key={entry.playerId}
                className="absolute h-4 w-4 rounded-full border-2 border-white shadow"
                style={{ ...markerStyle(entry.guess), backgroundColor: playerColor(index) }}
                title={`${entry.username}: ${entry.distancePercent}%`}
              />
            ))
            : null}
        </div>

        {phase === 'guessing' && !finished ? (
          <>
            <div className="mt-4 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
              <div className="h-3 bg-emerald-500 transition-[width] duration-100" style={{ width: `${(1 - (timeLeft.value / timeLimit)) * 100}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <span>{timeLeft.value}s left</span>
              <span>{mySubmitted ? 'Guess locked in.' : 'Tap image to place your marker.'}</span>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={submitSpot}
                disabled={!pendingSpot || mySubmitted}
                className="rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {mySubmitted ? 'Submitted' : 'Submit Guess'}
              </button>
              <button
                type="button"
                onClick={() => setPendingSpot(null)}
                disabled={!pendingSpot || mySubmitted}
                className="rounded-2xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Clear Marker
              </button>
            </div>
          </>
        ) : null}

        {phase === 'reveal' ? (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/80">
            <div className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Round Reveal</div>
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full border-separate border-spacing-y-2 text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                    <th className="px-3 py-2">Player</th>
                    <th className="px-3 py-2">Distance</th>
                    <th className="px-3 py-2">Time</th>
                    <th className="px-3 py-2">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {revealRows.map((row) => (
                    <tr key={row.playerId} className="rounded-2xl bg-slate-50 dark:bg-slate-800/70">
                      <td className="rounded-l-2xl px-3 py-2 font-semibold">{row.username}</td>
                      <td className="px-3 py-2 tabular-nums">{row.hadGuess ? `${row.distancePercent}%` : 'No guess'}</td>
                      <td className="px-3 py-2 tabular-nums">{formatTime(row.answerTime) || '—'}</td>
                      <td className="rounded-r-2xl px-3 py-2 font-semibold text-emerald-700 dark:text-emerald-300">+{row.award || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>

      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Leaderboard</div>
        <div className="mt-3 space-y-2">
          {rankings.map((entry, index) => (
            <div key={entry.socketId} className="flex items-center justify-between rounded-2xl bg-white px-3 py-2 text-sm shadow-sm dark:bg-slate-900">
              <div className="font-medium">#{index + 1} {entry.username}</div>
              <div className="font-semibold text-emerald-700 dark:text-emerald-300">{entry.score}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

SpotTheBallGame.Guide = function SpotTheBallGuide() {
  return (
    <div>
      <h3 className="mb-2 font-bold">Spot The Ball</h3>
      <p>Click where you think the missing football is, submit your marker, and score by closeness.</p>
    </div>
  );
};